import React, { useEffect, useState, useCallback } from "react";
import { publicApi } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import MerchCard from "@/components/public/MerchCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { MERCH_CATEGORIES, merchCategoryLabel } from "@/lib/format";

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get("/public/merch/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (category !== "all") params.category = category;
    try {
      const r = await publicApi.get("/public/merch", { params });
      setProducts(r.data);
    } finally {
      setLoading(false);
    }
  }, [category, sort]);
  useEffect(() => { load(); }, [load]);

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-5 py-14">
        <div className="text-center mb-10">
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">THE SHOP</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">Merch &amp; Accessories</h1>
          <p className="text-[var(--text-2)] mt-3 max-w-xl mx-auto">House apparel, leather goods, and watch-care essentials. A few pieces are reserved for VIP members.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-8 justify-center">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="sm:w-52 bg-[var(--panel)] border-[var(--border-2)] text-[var(--text)]" data-testid="merch-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {(categories.length ? categories : MERCH_CATEGORIES.map((c) => c.value)).map((c) => (
                <SelectItem key={c} value={c}>{merchCategoryLabel(c)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-48 bg-[var(--panel)] border-[var(--border-2)] text-[var(--text)]" data-testid="merch-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-[var(--panel)] border border-[var(--border-lch)] rounded-lg overflow-hidden">
                <Skeleton className="aspect-square w-full bg-[var(--panel-2)]" />
                <div className="p-4 space-y-2"><Skeleton className="h-3 w-1/3 bg-[var(--panel-2)]" /><Skeleton className="h-4 w-2/3 bg-[var(--panel-2)]" /><Skeleton className="h-8 w-full bg-[var(--panel-2)]" /></div>
              </div>
            ))}
          </div>
        ) : products.length ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="merch-grid">
            {products.map((p) => <MerchCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="text-center text-[var(--muted)] py-20" data-testid="merch-empty">No products in this category yet.</div>
        )}
      </section>
    </PublicLayout>
  );
}
